import type { ReactNode } from 'react';
import { Avatar, type AvatarSize } from '../Avatar';
import { StackedCell } from './StackedCell';
import { useListSurface } from './listSurface';

/**
 * Person cell — reference §14. Neutral `<Avatar>` beside a `<StackedCell>`.
 *
 * Person rows only. Initials, colour, and dimensions belong to `<Avatar>`, so every person
 * surface in the product renders identically.
 */
export function AvatarCell({
  primary,
  secondary,
  name,
  email,
  imageUrl,
  size = 'md',
  mono = false,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  /** Drives the initials. Falls back to `email`, then to a neutral dash. */
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  size?: AvatarSize;
  mono?: boolean;
}) {
  const inList = useListSurface();

  /*
    In the list view the avatar is hoisted out and drawn to the left of both lines, so this
    renders text only — otherwise the row would show two of them. See `listSurface.ts`.
  */
  if (inList) {
    return <StackedCell primary={primary} secondary={secondary} mono={mono} />;
  }

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={name} email={email} imageUrl={imageUrl} size={size} />
      <StackedCell primary={primary} secondary={secondary} mono={mono} />
    </div>
  );
}
